<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import SequenceItemCard from './SequenceItemCard.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';

const props = defineProps({
  templateId: {
    type: Number,
    default: null,
  },
  availableVariables: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['itemsChanged', 'requestAutoSave']);

// Pending add type — used when user clicks Add Item before template is saved
const pendingAddType = ref(null);

const store = useStore();
const { t } = useI18n();

const isLoading = ref(false);
const isDragging = ref(false);
const isAddingItem = ref(false);
const addItemType = ref('text');
const isSubmittingNewItem = ref(false);

// New item form
const newItemContent = ref('');
const newItemDelay = ref(0);
const newItemAttachment = ref(null);
const newItemFileInputRef = ref(null);

// Audio recorder
const isRecordingAudio = ref(false);
const recordingAudioDurationText = ref('00:00');
const audioRecorderRef = ref(null);
const recordedAudioFile = ref(null);
const recordedAudioBlobUrl = ref(null);
const audioPlayerRef = ref(null);
const recordingAudioState = ref('');
const audioRecordFormat = AUDIO_FORMATS.MP3;

const delayOptions = [
  { value: 0, label: 'FOLLOW_UP_ITEMS.DELAY.NO_DELAY' },
  { value: 5, label: 'FOLLOW_UP_ITEMS.DELAY.SECONDS_5' },
  { value: 10, label: 'FOLLOW_UP_ITEMS.DELAY.SECONDS_10' },
  { value: 30, label: 'FOLLOW_UP_ITEMS.DELAY.SECONDS_30' },
  { value: 60, label: 'FOLLOW_UP_ITEMS.DELAY.MINUTE_1' },
  { value: 300, label: 'FOLLOW_UP_ITEMS.DELAY.MINUTES_5' },
  { value: 600, label: 'FOLLOW_UP_ITEMS.DELAY.MINUTES_10' },
];

const items = computed(() =>
  store.getters['followUpTemplates/getItems'](props.templateId)
);

const fetchItems = async () => {
  if (!props.templateId) return;
  isLoading.value = true;
  try {
    await store.dispatch('followUpTemplates/fetchItems', props.templateId);
  } catch {
    useAlert(t('FOLLOW_UP_ITEMS.MESSAGES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.templateId,
  async newId => {
    if (newId) {
      await fetchItems();
    }
  },
  { immediate: true }
);

// Drag & drop reorder
const handleDragEnd = async () => {
  isDragging.value = false;
  const reorderedItems = items.value.map(item => ({
    id: item.id,
    delay_seconds: item.delay_seconds,
  }));

  try {
    await store.dispatch('followUpTemplates/reorderItems', {
      templateId: props.templateId,
      items: reorderedItems,
    });
    emit('itemsChanged');
  } catch {
    useAlert(t('FOLLOW_UP_ITEMS.MESSAGES.REORDER_ERROR'));
    await fetchItems();
  }
};

// Define cancelRecordedAudio before startAddItem to avoid use-before-define
const cancelRecordedAudio = () => {
  if (recordedAudioBlobUrl.value) {
    URL.revokeObjectURL(recordedAudioBlobUrl.value);
    recordedAudioBlobUrl.value = null;
  }
  recordedAudioFile.value = null;
  recordingAudioDurationText.value = '00:00';
  recordingAudioState.value = '';
  isRecordingAudio.value = false;
};

// Add item helpers
const startAddItem = type => {
  addItemType.value = type;
  isAddingItem.value = true;
  newItemContent.value = '';
  newItemDelay.value = 0;
  newItemAttachment.value = null;
  cancelRecordedAudio();
};

const cancelAdd = () => {
  isAddingItem.value = false;
  newItemAttachment.value = null;
  cancelRecordedAudio();
};

const triggerNewFileInput = () => {
  newItemFileInputRef.value?.click();
};

const handleNewFileSelect = event => {
  const file = event.target.files[0];
  if (!file) return;

  const maxSize = 20 * 1024 * 1024;
  if (file.size > maxSize) {
    useAlert(t('FOLLOW_UP_ITEMS.MESSAGES.FILE_TOO_LARGE'));
    return;
  }

  newItemAttachment.value = file;

  // Auto-detect type
  if (file.type.startsWith('image/')) addItemType.value = 'image';
  else if (file.type.startsWith('audio/')) addItemType.value = 'audio';
  else if (file.type.startsWith('video/')) addItemType.value = 'video';
  else addItemType.value = 'document';

  event.target.value = '';
};

const removeNewAttachment = () => {
  newItemAttachment.value = null;
};

const insertVariable = variable => {
  newItemContent.value += variable;
};

// Audio recorder methods
const toggleAudioRecorder = () => {
  if (isRecordingAudio.value) {
    if (audioRecorderRef.value) {
      audioRecorderRef.value.stopRecording();
    }
  } else {
    recordedAudioFile.value = null;
    recordingAudioDurationText.value = '00:00';
    recordingAudioState.value = '';
    isRecordingAudio.value = true;
  }
};

// Intercepts Add Item clicks when no templateId yet.
// Stores the requested type and emits @request-auto-save to parent,
// which creates the template; the watcher then opens the add form.
const requestAddItem = (type, withAudio = false) => {
  if (!props.templateId) {
    pendingAddType.value = type;
    emit('requestAutoSave');
  } else {
    startAddItem(type);
    if (withAudio) toggleAudioRecorder();
  }
};

// After auto-save, templateId prop changes — open pending add form
watch(
  () => props.templateId,
  newId => {
    if (newId && pendingAddType.value) {
      const type = pendingAddType.value;
      pendingAddType.value = null;
      startAddItem(type);
      if (type === 'audio') toggleAudioRecorder();
    }
  }
);

const onRecordProgressChanged = duration => {
  recordingAudioDurationText.value = duration;
};

const onFinishRecorder = file => {
  recordingAudioState.value = 'stopped';
  recordedAudioFile.value = file;
  if (file.file) {
    recordedAudioBlobUrl.value = URL.createObjectURL(file.file);
  }
  isRecordingAudio.value = false;
  // Set as attachment
  newItemAttachment.value = file.file;
  addItemType.value = 'audio';
};

const toggleAudioPlayPause = () => {
  if (audioPlayerRef.value && recordedAudioBlobUrl.value) {
    if (recordingAudioState.value === 'playing') {
      audioPlayerRef.value.pause();
      recordingAudioState.value = 'paused';
    } else {
      audioPlayerRef.value.play();
      recordingAudioState.value = 'playing';
    }
  }
};

const onAudioEnded = () => {
  recordingAudioState.value = 'stopped';
};

const formatFileSize = bytes => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const canCreateItem = computed(() => {
  if (addItemType.value === 'text') {
    return newItemContent.value.trim().length > 0;
  }
  return newItemAttachment.value !== null || recordedAudioFile.value !== null;
});

const handleCreateItem = async () => {
  if (!canCreateItem.value) {
    if (addItemType.value === 'text') {
      useAlert(t('FOLLOW_UP_ITEMS.MESSAGES.CONTENT_REQUIRED'));
    } else {
      useAlert(t('FOLLOW_UP_ITEMS.MESSAGES.ATTACHMENT_REQUIRED'));
    }
    return;
  }

  isSubmittingNewItem.value = true;
  try {
    await store.dispatch('followUpTemplates/createItem', {
      templateId: props.templateId,
      data: {
        item_type: addItemType.value,
        content: newItemContent.value,
        delay_seconds: newItemDelay.value,
        position: items.value.length,
      },
      attachment: newItemAttachment.value,
    });
    useAlert(t('FOLLOW_UP_ITEMS.MESSAGES.CREATE_SUCCESS'));
    cancelAdd();
    emit('itemsChanged');
  } catch (error) {
    const errorMessage =
      error.response?.data?.errors?.join(', ') ||
      t('FOLLOW_UP_ITEMS.MESSAGES.ERROR');
    useAlert(errorMessage);
  } finally {
    isSubmittingNewItem.value = false;
  }
};

const onItemUpdated = () => {
  emit('itemsChanged');
};

const onItemDeleted = () => {
  emit('itemsChanged');
};
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <div>
        <h3 class="text-sm font-semibold text-slate-800 dark:text-slate-200">
          {{ t('FOLLOW_UP_BUILDER.SEQUENCE.TITLE') }}
        </h3>
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {{ t('FOLLOW_UP_BUILDER.SEQUENCE.DESCRIPTION') }}
        </p>
      </div>
      <span
        v-if="items.length > 0"
        class="text-xs font-medium text-slate-500 dark:text-slate-400"
      >
        {{ items.length }}
        {{
          items.length === 1
            ? t('FOLLOW_UP_BUILDER.SEQUENCE.ITEM')
            : t('FOLLOW_UP_BUILDER.SEQUENCE.ITEMS')
        }}
      </span>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div
        class="w-8 h-8 border-4 border-slate-200 dark:border-slate-700 border-t-woot-500 rounded-full animate-spin"
      />
    </div>

    <template v-else>
      <!-- Items List -->
      <div class="flex-1 overflow-y-auto space-y-2 mb-4">
        <!-- Empty state -->
        <div
          v-if="items.length === 0 && !isAddingItem"
          class="text-center py-10 px-4 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg"
        >
          <span
            class="i-lucide-layers size-10 text-slate-300 dark:text-slate-600 mb-3 mx-auto block"
          />
          <p class="text-sm text-slate-500 dark:text-slate-400 mb-3">
            {{ t('FOLLOW_UP_BUILDER.SEQUENCE.EMPTY') }}
          </p>
        </div>

        <!-- Draggable items -->
        <Draggable
          v-else
          v-model="items"
          item-key="id"
          handle=".drag-handle"
          ghost-class="opacity-50"
          @start="isDragging = true"
          @end="handleDragEnd"
        >
          <template #item="{ element: item, index }">
            <SequenceItemCard
              :item="item"
              :index="index"
              :template-id="templateId"
              :available-variables="availableVariables"
              :delay-options="delayOptions"
              @updated="onItemUpdated"
              @deleted="onItemDeleted"
            />
          </template>
        </Draggable>
      </div>

      <!-- Add Item Form (inline) -->
      <div
        v-if="isAddingItem"
        class="border-2 border-dashed border-woot-300 dark:border-woot-700 rounded-lg p-4 mb-4 bg-woot-50/30 dark:bg-woot-900/10"
      >
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300">
            {{ t('FOLLOW_UP_BUILDER.SEQUENCE.NEW_ITEM') }}
          </h4>
          <button
            type="button"
            class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            @click="cancelAdd"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Hidden file input -->
        <input
          ref="newItemFileInputRef"
          type="file"
          accept="image/*,audio/*,video/mp4,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
          class="hidden"
          @change="handleNewFileSelect"
        />

        <!-- Text Item -->
        <div v-if="addItemType === 'text'" class="space-y-3">
          <textarea
            v-model="newItemContent"
            rows="3"
            :placeholder="t('FOLLOW_UP_ITEMS.MODAL.CONTENT_PLACEHOLDER')"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-woot-500 focus:border-woot-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 resize-none text-sm"
          />
          <div
            v-if="availableVariables.length > 0"
            class="flex flex-wrap gap-1"
          >
            <button
              v-for="variable in availableVariables"
              :key="variable"
              type="button"
              class="px-2 py-0.5 text-xs bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-slate-500 transition-colors"
              @click="insertVariable(variable)"
            >
              {{ variable }}
            </button>
          </div>
        </div>

        <!-- Media Item (file upload) -->
        <div
          v-else-if="
            addItemType !== 'text' && !isRecordingAudio && !recordedAudioFile
          "
          class="space-y-3"
        >
          <!-- Pending attachment -->
          <div
            v-if="newItemAttachment"
            class="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
          >
            <span class="i-lucide-paperclip size-5 text-blue-500" />
            <div class="flex-1 min-w-0">
              <p
                class="text-sm font-medium text-slate-700 dark:text-slate-300 truncate"
              >
                {{ newItemAttachment.name }}
              </p>
              <p class="text-xs text-slate-500">
                {{ formatFileSize(newItemAttachment.size) }}
              </p>
            </div>
            <button
              type="button"
              class="p-1 text-slate-400 hover:text-red-500"
              @click="removeNewAttachment"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <!-- Upload prompt -->
          <button
            v-else
            type="button"
            class="w-full px-4 py-6 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg hover:border-slate-400 transition-colors flex flex-col items-center justify-center gap-2 text-slate-500"
            @click="triggerNewFileInput"
          >
            <span class="i-lucide-upload size-6" />
            <span class="text-sm font-medium">
              {{ t('FOLLOW_UP_ITEMS.MODAL.UPLOAD_FILE') }}
            </span>
          </button>

          <!-- Optional caption for image/video -->
          <input
            v-if="
              newItemAttachment &&
              (addItemType === 'image' || addItemType === 'video')
            "
            v-model="newItemContent"
            type="text"
            :placeholder="t('FOLLOW_UP_BUILDER.ITEMS.CAPTION_PLACEHOLDER')"
            class="w-full h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-woot-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 text-sm"
          />
        </div>

        <!-- Audio Recording -->
        <div
          v-if="addItemType === 'audio' && isRecordingAudio"
          class="space-y-3"
        >
          <div class="p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
            <div class="flex items-center gap-3 mb-3">
              <span class="i-lucide-mic size-5 text-red-500 animate-pulse" />
              <span
                class="text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{ t('FOLLOW_UP_ITEMS.MODAL.RECORDING') }}
              </span>
              <span class="text-sm text-slate-500">
                {{ recordingAudioDurationText }}
              </span>
              <button
                type="button"
                class="ml-auto flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                @click="toggleAudioRecorder"
              >
                <span class="i-lucide-square size-3.5" />
                {{ t('FOLLOW_UP_ITEMS.MODAL.STOP_RECORDING') }}
              </button>
            </div>
            <AudioRecorder
              ref="audioRecorderRef"
              :audio-record-format="audioRecordFormat"
              @recorder-progress-changed="onRecordProgressChanged"
              @finish-record="onFinishRecorder"
            />
          </div>
        </div>

        <!-- Recorded Audio Preview -->
        <div v-if="recordedAudioFile && !isRecordingAudio" class="space-y-3">
          <audio
            ref="audioPlayerRef"
            :src="recordedAudioBlobUrl"
            class="hidden"
            @ended="onAudioEnded"
          />
          <div
            class="p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="i-lucide-music size-5 text-teal-600" />
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ t('FOLLOW_UP_ITEMS.MODAL.RECORDED_AUDIO') }}
                </span>
                <span class="text-xs text-slate-500">
                  ({{ recordingAudioDurationText }})
                </span>
              </div>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="p-1.5 text-slate-500 hover:text-slate-700"
                  @click="toggleAudioPlayPause"
                >
                  <span
                    class="size-4"
                    :class="
                      recordingAudioState === 'playing'
                        ? 'i-lucide-pause'
                        : 'i-lucide-play'
                    "
                  />
                </button>
                <button
                  type="button"
                  class="p-1.5 text-slate-500 hover:text-red-500"
                  @click="cancelRecordedAudio"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Delay -->
        <div class="mt-3">
          <label
            class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
          >
            {{ t('FOLLOW_UP_ITEMS.MODAL.DELAY_LABEL') }}
          </label>
          <select
            v-model.number="newItemDelay"
            class="w-full h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-woot-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm"
          >
            <option
              v-for="option in delayOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ t(option.label) }}
            </option>
          </select>
        </div>

        <!-- Save button -->
        <div class="flex gap-2 mt-3">
          <NextButton
            size="sm"
            variant="faded"
            color="slate"
            class="flex-1"
            @click="cancelAdd"
          >
            {{ t('FOLLOW_UP_ITEMS.MODAL.CANCEL') }}
          </NextButton>
          <NextButton
            size="sm"
            variant="solid"
            color="blue"
            class="flex-1"
            :is-loading="isSubmittingNewItem"
            :is-disabled="!canCreateItem"
            @click="handleCreateItem"
          >
            {{ t('FOLLOW_UP_ITEMS.MODAL.CREATE') }}
          </NextButton>
        </div>
      </div>

      <!-- Add Item Toolbar -->
      <div
        v-if="!isAddingItem"
        class="flex-shrink-0 pt-3 border-t border-slate-200 dark:border-slate-700"
      >
        <p class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
          {{ t('FOLLOW_UP_BUILDER.SEQUENCE.ADD_ITEM') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <NextButton
            size="xs"
            variant="faded"
            color="slate"
            icon="type"
            @click="requestAddItem('text')"
          >
            {{ t('FOLLOW_UP_ITEMS.TYPES.TEXT') }}
          </NextButton>
          <NextButton
            size="xs"
            variant="faded"
            color="slate"
            icon="image"
            @click="requestAddItem('image')"
          >
            {{ t('FOLLOW_UP_ITEMS.TYPES.IMAGE') }}
          </NextButton>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            @click="requestAddItem('audio', true)"
          >
            <span class="i-lucide-mic size-3.5" />
            {{ t('FOLLOW_UP_ITEMS.TYPES.AUDIO') }}
          </button>
          <NextButton
            size="xs"
            variant="faded"
            color="slate"
            icon="video"
            @click="requestAddItem('video')"
          >
            {{ t('FOLLOW_UP_ITEMS.TYPES.VIDEO') }}
          </NextButton>
          <NextButton
            size="xs"
            variant="faded"
            color="slate"
            icon="file-text"
            @click="requestAddItem('document')"
          >
            {{ t('FOLLOW_UP_ITEMS.TYPES.DOCUMENT') }}
          </NextButton>
        </div>
      </div>
    </template>
  </div>
</template>
